"use client"

import type React from "react"
import { useEffect, useState, useCallback, useRef } from "react"
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  FormGroup,
  Label,
  Input,
  Spinner,
  FormFeedback,
  Card,
  CardBody,
  Row,
  Col,
  Alert,
} from "reactstrap"
import ExhibitorsService from "../../../Service/ExhibitorsService"
import ExhibitorTypeService from "../../../Service/ExhibitorTypeService"
import type { Exhibitors, Country } from "./types"
import { toast } from "react-toastify"
import type { ExhibitorType } from "../ExhibitorType/types"
import Swal from "sweetalert2"
import {
  FaUpload,
  FaFile,
  FaTimes,
  FaCheck,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaIdCard,
  FaGlobe,
  FaSearch,
  FaChevronDown,
  FaExclamationTriangle,
} from "react-icons/fa"

interface Props {
  isOpen: boolean
  toggle: () => void
  onSuccess: () => void
  initialData: Exhibitors | null
}

interface ValidationErrors {
  [key: string]: string | string[]
}

const ExhibitorsModal = ({ isOpen, toggle, onSuccess, initialData }: Props) => {
  const [form, setForm] = useState<Omit<Exhibitors, "id">>({
    name: "",
    tin: 0,
    email: "",
    phone: 0,
    type: 0,
    country: 0,
    status: true,
    postpaid: false,
    is_host: false,
    postpaid_document: null,
  })
  const [types, setTypes] = useState<ExhibitorType[]>([])
  const [countries, setCountries] = useState<Country[]>([])
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<ValidationErrors>({})
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [dragActive, setDragActive] = useState(false)
  // Country search states
  const [countrySearch, setCountrySearch] = useState("")
  const [showCountryDropdown, setShowCountryDropdown] = useState(false)
  const [filteredCountries, setFilteredCountries] = useState<Country[]>([])
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null)
  const countryDropdownRef = useRef<HTMLDivElement>(null)
  const countryInputRef = useRef<any>(null)

  useEffect(() => {
    if (initialData) {
      setForm({
        name: initialData.name,
        tin: initialData.tin,
        email: initialData.email,
        phone: initialData.phone,
        type: initialData.type,
        country: initialData.country,
        status: initialData.status,
        postpaid: initialData.postpaid,
        is_host: initialData.is_host,
        postpaid_document: initialData.postpaid_document,
      })
      // Set selected country for display
      if (initialData.country && initialData.country_name) {
        setSelectedCountry({
          id: initialData.country,
          name: initialData.country_name,
          code: "", // We don't have the code from initialData
        })
        setCountrySearch(initialData.country_name)
      }
    } else {
      setForm({
        name: "",
        tin: 0,
        email: "",
        phone: 0,
        type: 0,
        country: 0,
        status: true,
        postpaid: false,
        is_host: false,
        postpaid_document: null,
      })
      setSelectedCountry(null)
      setCountrySearch("")
    }
    setSelectedFile(null)
    setErrors({}) // Clear errors when modal opens/closes
  }, [initialData, isOpen])

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [typesData, countriesData] = await Promise.all([
          ExhibitorTypeService.getTypes(),
          ExhibitorsService.getAllCountries(),
        ])
        setTypes(typesData)
        setCountries(countriesData)
        setFilteredCountries(countriesData)
      } catch {
        toast.error("Failed to load data")
      }
    }
    fetchData()
  }, [])

  // Filter countries based on search
  useEffect(() => {
    if (countrySearch.trim() === "") {
      setFilteredCountries(countries)
    } else {
      const filtered = countries.filter(
        (country) =>
          country.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
          country.code.toLowerCase().includes(countrySearch.toLowerCase()),
      )
      setFilteredCountries(filtered)
    }
  }, [countrySearch, countries])

  // Handle clicks outside country dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target as Node)) {
        setShowCountryDropdown(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target
    setForm({
      ...form,
      [name]: type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    })
    // Clear specific field error when user starts typing
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }

  const handleCountrySearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setCountrySearch(value)
    setShowCountryDropdown(true)
    // Clear selection if user is typing
    if (selectedCountry && value !== selectedCountry.name) {
      setSelectedCountry(null)
      setForm((prev) => ({ ...prev, country: 0 }))
    }
    if (errors.country) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors.country
        return newErrors
      })
    }
  }

  const handleCountrySelect = (country: Country) => {
    setSelectedCountry(country)
    setCountrySearch(country.name)
    setForm((prev) => ({ ...prev, country: country.id }))
    setShowCountryDropdown(false)
    if (errors.country) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors.country
        return newErrors
      })
    }
  }

  const handleCountryInputFocus = () => {
    setShowCountryDropdown(true)
  }

  const handleFileChange = (file: File | null) => {
    setSelectedFile(file)
    if (errors.postpaid_document) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors.postpaid_document
        return newErrors
      })
    }
  }

  const handleInputFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null
    handleFileChange(file)
  }

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true)
    } else if (e.type === "dragleave") {
      setDragActive(false)
    }
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0]
      const allowedTypes = [".pdf", ".doc", ".docx", ".jpg", ".jpeg", ".png"]
      const fileExtension = "." + file.name.split(".").pop()?.toLowerCase()
      if (allowedTypes.includes(fileExtension)) {
        handleFileChange(file)
      } else {
        toast.error("Please upload a valid file type (PDF, DOC, DOCX, JPG, JPEG, PNG)")
      }
    }
  }, [])

  const removeFile = () => {
    setSelectedFile(null)
  }

  const validateForm = () => {
    const newErrors: ValidationErrors = {}

    if (!form.name.trim()) {
      newErrors.name = "Name is required"
    }

    if (!form.email.trim()) {
      newErrors.email = "Email is required"
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      newErrors.email = "Please enter a valid email address"
    }

    if (!form.phone || form.phone <= 0) {
      newErrors.phone = "Phone number is required"
    }

    if (!form.type) {
      newErrors.type = "Exhibitor type is required"
    }

    if (!form.country) {
      newErrors.country = "Country is required"
    }

    if (form.postpaid && !selectedFile && !form.postpaid_document) {
      newErrors.postpaid_document = "Postpaid document is required when postpaid is enabled"
    }

    return newErrors
  }

  // Helper function to get error message
  const getErrorMessage = (fieldName: string): string => {
    const error = errors[fieldName]
    if (Array.isArray(error)) {
      return error[0]
    }
    return error || ""
  }

  // Helper function to check if field has error
  const hasError = (fieldName: string): boolean => {
    return !!errors[fieldName]
  }

  const handleSubmit = async () => {
    const validationErrors = validateForm()
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }

    try {
      setLoading(true)
      setErrors({}) // Clear any previous errors

      const formData = new FormData()
      formData.append("name", form.name)
      formData.append("tin", form.tin.toString())
      formData.append("email", form.email)
      formData.append("phone", form.phone.toString())
      formData.append("type", form.type.toString())
      formData.append("country", form.country.toString())
      formData.append("status", form.status.toString())
      formData.append("postpaid", form.postpaid.toString())
      formData.append("is_host", form.is_host.toString())

      if (selectedFile) {
        formData.append("postpaid_document", selectedFile)
      }

      if (initialData) {
        await ExhibitorsService.update(initialData.id, formData)
        await Swal.fire("Updated!", "Exhibitor updated successfully.", "success")
      } else {
        await ExhibitorsService.create(formData)
        await Swal.fire("Created!", "Exhibitor created successfully.", "success")
      }

      onSuccess()
      toggle()
    } catch (error: any) {
      console.error("API Error:", error)

      // Handle validation errors from backend
      if (error.response && error.response.status === 400) {
        const backendErrors = error.response.data
        if (typeof backendErrors === "object" && backendErrors !== null) {
          setErrors(backendErrors)

          // Show specific error messages for uniqueness violations
          const errorMessages = []
          if (backendErrors.email) {
            errorMessages.push(
              `Email: ${Array.isArray(backendErrors.email) ? backendErrors.email[0] : backendErrors.email}`,
            )
          }
          if (backendErrors.phone) {
            errorMessages.push(
              `Phone: ${Array.isArray(backendErrors.phone) ? backendErrors.phone[0] : backendErrors.phone}`,
            )
          }
          if (backendErrors.tin) {
            errorMessages.push(`TIN: ${Array.isArray(backendErrors.tin) ? backendErrors.tin[0] : backendErrors.tin}`)
          }

          if (errorMessages.length > 0) {
            // toast.error(`Validation Error: ${errorMessages.join(", ")}`)
          } else {
            toast.error("Please check the form for errors")
          }
        } else {
          toast.error("Validation failed. Please check your input.")
        }
      } else {
        await Swal.fire("Error", "Something went wrong. Please try again.", "error")
      }
    } finally {
      setLoading(false)
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes"
    const k = 1024
    const sizes = ["Bytes", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" className="exhibitor-modal">
      <ModalHeader toggle={toggle} className="bg-primary text-white">
        <div className="d-flex align-items-center">
          <FaUser className="me-2" />
          {initialData ? "Edit Exhibitor" : "Add New Exhibitor"}
        </div>
      </ModalHeader>
      <ModalBody className="p-4">
        <div className="container-fluid">
          

          {/* Basic Information Section */}
          <Card className="mb-4 shadow-sm">
            <CardBody>
              <h5 className="card-title text-primary mb-3">
                <FaUser className="me-2" />
                Basic Information
              </h5>
              <div className="row g-3">
                <div className="col-md-6">
                  <FormGroup>
                    <Label for="name" className="fw-semibold">
                      <FaUser className="me-1 text-muted" size={14} />
                      Full Name *
                    </Label>
                    <Input
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      invalid={hasError("name")}
                      className="form-control-lg"
                      placeholder="Enter full name"
                    />
                    <FormFeedback>{getErrorMessage("name")}</FormFeedback>
                  </FormGroup>
                </div>
                <div className="col-md-6">
                  <FormGroup>
                    <Label for="tin" className="fw-semibold">
                      <FaIdCard className="me-1 text-muted" size={14} />
                      TIN Number *
                    </Label>
                    <Input
                      type="number"
                      name="tin"
                      value={form.tin}
                      onChange={handleChange}
                      invalid={hasError("tin")}
                      className="form-control-lg"
                      placeholder="Enter TIN number"
                    />
                    <FormFeedback>{getErrorMessage("tin")}</FormFeedback>
                  </FormGroup>
                </div>
              </div>
              <div className="row g-3">
                <div className="col-md-6">
                  <FormGroup>
                    <Label for="email" className="fw-semibold">
                      <FaEnvelope className="me-1 text-muted" size={14} />
                      Email Address *
                    </Label>
                    <Input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      invalid={hasError("email")}
                      className="form-control-lg"
                      placeholder="Enter email address"
                    />
                    <FormFeedback>{getErrorMessage("email")}</FormFeedback>
                  </FormGroup>
                </div>
                <div className="col-md-6">
                  <FormGroup>
                    <Label for="phone" className="fw-semibold">
                      <FaPhone className="me-1 text-muted" size={14} />
                      Phone Number *
                    </Label>
                    <Input
                      type="number"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      invalid={hasError("phone")}
                      className="form-control-lg"
                      placeholder="Enter phone number"
                    />
                    <FormFeedback>{getErrorMessage("phone")}</FormFeedback>
                  </FormGroup>
                </div>
              </div>
              <div className="row g-3">
                <div className="col-md-6">
                  <FormGroup>
                    <Label for="country" className="fw-semibold">
                      <FaGlobe className="me-1 text-muted" size={14} />
                      Country *
                    </Label>
                    <div className="position-relative" ref={countryDropdownRef}>
                      <div className="input-group">
                        <span className="input-group-text">
                          <FaSearch className="text-muted" size={14} />
                        </span>
                        <Input
                          type="text"
                          value={countrySearch}
                          onChange={handleCountrySearch}
                          onFocus={handleCountryInputFocus}
                          invalid={hasError("country")}
                          className="form-control-lg"
                          placeholder="Search and select country..."
                          autoComplete="off"
                        />
                        <span className="input-group-text">
                          <FaChevronDown
                            className={`text-muted transition-transform ${showCountryDropdown ? "rotate-180" : ""}`}
                            size={14}
                          />
                        </span>
                      </div>
                      {showCountryDropdown && (
                        <div className="country-dropdown">
                          <div className="dropdown-header">
                            <small className="text-muted">{filteredCountries.length} countries found</small>
                          </div>
                          <div className="dropdown-body">
                            {filteredCountries.length > 0 ? (
                              filteredCountries.map((country) => (
                                <div
                                  key={country.id}
                                  className={`dropdown-item ${selectedCountry?.id === country.id ? "selected" : ""}`}
                                  onClick={() => handleCountrySelect(country)}
                                >
                                  <div className="d-flex justify-content-between align-items-center">
                                    <span className="country-name">{country.name}</span>
                                    <small className="country-code text-muted">{country.code}</small>
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="dropdown-item disabled">
                                <span className="text-muted">No countries found</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    <FormFeedback>{getErrorMessage("country")}</FormFeedback>
                  </FormGroup>
                </div>
                <div className="col-md-6">
                  <FormGroup>
                    <Label for="type" className="fw-semibold">
                      Exhibitor Type *
                    </Label>
                    <Input
                      type="select"
                      name="type"
                      value={form.type}
                      onChange={handleChange}
                      invalid={hasError("type")}
                      className="form-control-lg"
                    >
                      <option value="">-- Select Type --</option>
                      {types.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </Input>
                    <FormFeedback>{getErrorMessage("type")}</FormFeedback>
                  </FormGroup>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Status Configuration Section */}
          <Card className="mb-4 shadow-sm">
            <CardBody>
              <h5 className="card-title text-primary mb-3">Status Configuration</h5>
              <Row className="g-3">
                <Col md="4">
                  <label
                    htmlFor="status"
                    className={`toggle-card ${form.status ? "toggle-card-on toggle-success" : ""}`}
                  >
                    <span className="toggle-card-text">
                      <span className="toggle-card-title">{form.status ? "Active" : "Inactive"}</span>
                      <span className="toggle-card-subtitle">Visible to the public</span>
                    </span>
                    <span className={`toggle-switch ${form.status ? "toggle-switch-on toggle-success" : ""}`}>
                      <input
                        type="checkbox"
                        id="status"
                        name="status"
                        checked={form.status}
                        onChange={handleChange}
                        className="toggle-switch-input"
                      />
                      <span className="toggle-switch-knob" />
                    </span>
                  </label>
                </Col>
                <Col md="4">
                  <label
                    htmlFor="postpaid"
                    className={`toggle-card ${form.postpaid ? "toggle-card-on toggle-info" : ""}`}
                  >
                    <span className="toggle-card-text">
                      <span className="toggle-card-title">{form.postpaid ? "Postpaid" : "Prepaid"}</span>
                      <span className="toggle-card-subtitle">
                        {form.postpaid ? "Requires supporting document" : "No document required"}
                      </span>
                    </span>
                    <span className={`toggle-switch ${form.postpaid ? "toggle-switch-on toggle-info" : ""}`}>
                      <input
                        type="checkbox"
                        id="postpaid"
                        name="postpaid"
                        checked={form.postpaid}
                        onChange={handleChange}
                        className="toggle-switch-input"
                      />
                      <span className="toggle-switch-knob" />
                    </span>
                  </label>
                </Col>
                <Col md="4">
                  <label
                    htmlFor="is_host"
                    className={`toggle-card ${form.is_host ? "toggle-card-on toggle-primary" : ""}`}
                  >
                    <span className="toggle-card-text">
                      <span className="toggle-card-title">{form.is_host ? "Is Host" : "Not Host"}</span>
                      <span className="toggle-card-subtitle">Hosting the exhibition</span>
                    </span>
                    <span className={`toggle-switch ${form.is_host ? "toggle-switch-on toggle-primary" : ""}`}>
                      <input
                        type="checkbox"
                        id="is_host"
                        name="is_host"
                        checked={form.is_host}
                        onChange={handleChange}
                        className="toggle-switch-input"
                      />
                      <span className="toggle-switch-knob" />
                    </span>
                  </label>
                </Col>
              </Row>
            </CardBody>
          </Card>

          {/* Document Upload Section */}
          {form.postpaid && (
            <Card className="mb-4 shadow-sm">
              <CardBody>
                <h5 className="card-title text-primary mb-3">
                  <FaFile className="me-2" />
                  Postpaid Document
                </h5>
                <div
                  className={`drag-drop-zone ${dragActive ? "drag-active" : ""} ${hasError("postpaid_document") ? "is-invalid" : ""}`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  style={{
                    border: `2px dashed ${dragActive ? "#007bff" : hasError("postpaid_document") ? "#dc3545" : "#dee2e6"}`,
                    borderRadius: "12px",
                    padding: "2rem",
                    textAlign: "center",
                    backgroundColor: dragActive ? "#f8f9ff" : "#fafafa",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    position: "relative",
                  }}
                >
                  <input
                    type="file"
                    onChange={handleInputFileChange}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      opacity: 0,
                      cursor: "pointer",
                    }}
                  />
                  {selectedFile ? (
                    <div className="selected-file-info">
                      <div className="d-flex align-items-center justify-content-center mb-2">
                        <FaFile className="text-success me-2" size={24} />
                        <span className="fw-semibold text-success">File Selected</span>
                      </div>
                      <div className="file-details bg-white p-3 rounded shadow-sm">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <div className="fw-semibold">{selectedFile.name}</div>
                            <small className="text-muted">{formatFileSize(selectedFile.size)}</small>
                          </div>
                          <Button
                            color="danger"
                            size="sm"
                            outline
                            onClick={(e) => {
                              e.stopPropagation()
                              removeFile()
                            }}
                          >
                            <FaTimes />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="upload-placeholder">
                      <FaUpload className="text-primary mb-2" size={32} />
                      <div className="fw-semibold mb-1">
                        {dragActive ? "Drop your file here" : "Drag & drop your file here"}
                      </div>
                      <div className="text-muted mb-2">or click to browse</div>
                      <small className="text-muted">Supported formats: PDF, DOC, DOCX, JPG, JPEG, PNG</small>
                    </div>
                  )}
                </div>
                {hasError("postpaid_document") && (
                  <div className="invalid-feedback d-block mt-2">{getErrorMessage("postpaid_document")}</div>
                )}
                {form.postpaid_document && !selectedFile && (
                  <div className="current-document mt-3 p-3 bg-primary rounded">
                    <div className="d-flex align-items-center">
                      <FaFile className="text-info me-2" />
                      <div>
                        <small className="text-white d-block">Current document:</small>
                        <span className="fw-semibold">{form.postpaid_document.split("/").pop()}</span>
                      </div>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          )}

          {/* Action Buttons */}
          <div className="d-flex justify-content-end gap-2 pt-3 border-top">
            <Button color="secondary" onClick={toggle} disabled={loading}>
              Cancel
            </Button>
            <Button color="primary" onClick={handleSubmit} disabled={loading} className="px-4">
              {loading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  {initialData ? "Updating..." : "Creating..."}
                </>
              ) : (
                <>
                  <FaCheck className="me-2" />
                  {initialData ? "Update Exhibitor" : "Create Exhibitor"}
                </>
              )}
            </Button>
          </div>
        </div>
      </ModalBody>
      <style>{`
        .exhibitor-modal .modal-dialog {
          max-width: 900px;
        }
        
        .drag-drop-zone {
          min-height: 150px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .drag-active {
          transform: scale(1.02);
          box-shadow: 0 8px 25px rgba(0, 123, 255, 0.15);
        }
        
        .form-control-lg {
          border-radius: 8px;
          border: 1.5px solid #dee2e6;
          transition: all 0.2s ease;
        }
        
        .form-control-lg:focus {
          border-color: #007bff;
          box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
        }
        
        .card {
          border: none;
          border-radius: 12px;
        }
        
        .card-title {
          font-size: 1.1rem;
          margin-bottom: 1rem;
          padding-bottom: 0.5rem;
          border-bottom: 2px solid #f8f9fa;
        }
        
        .form-check-input:checked {
          background-color: #007bff;
          border-color: #007bff;
        }
        
        .selected-file-info .file-details {
          max-width: 300px;
          margin: 0 auto;
        }
        
        .country-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background: white;
          border: 1px solid #dee2e6;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          z-index: 1050;
          max-height: 300px;
          overflow: hidden;
        }
        
        .dropdown-header {
          padding: 8px 12px;
          background-color: #f8f9fa;
          border-bottom: 1px solid #dee2e6;
        }
        
        .dropdown-body {
          max-height: 250px;
          overflow-y: auto;
        }
        
        .dropdown-item {
          padding: 10px 12px;
          cursor: pointer;
          border-bottom: 1px solid #f8f9fa;
          transition: background-color 0.2s ease;
        }
        
        .dropdown-item:hover {
          background-color: #f8f9fa;
        }
        
        .dropdown-item.selected {
          background-color: #e3f2fd;
          color: #1976d2;
        }
        
        .dropdown-item.disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .dropdown-item:last-child {
          border-bottom: none;
        }
        
        .country-name {
          font-weight: 500;
        }
        
        .country-code {
          font-size: 0.85em;
          background-color: #e9ecef;
          padding: 2px 6px;
          border-radius: 4px;
        }
        
        .transition-transform {
          transition: transform 0.2s ease;
        }
        
        .rotate-180 {
          transform: rotate(180deg);
        }
        
        /* Custom scrollbar for dropdown */
        .dropdown-body::-webkit-scrollbar {
          width: 6px;
        }
        
        .dropdown-body::-webkit-scrollbar-track {
          background: #f1f1f1;
        }
        
        .dropdown-body::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 3px;
        }
        
        .dropdown-body::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }

        /* Toggle cards */
        .toggle-card {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          height: 100%;
          padding: 14px 16px;
          border: 1.5px solid #e9ecef;
          border-radius: 10px;
          background-color: #fafafa;
          cursor: pointer;
          transition: all 0.2s ease;
          margin: 0;
        }

        .toggle-card:hover {
          border-color: #ced4da;
          background-color: #f5f5f5;
        }

        .toggle-card-on {
          background-color: #ffffff;
          border-color: currentColor;
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
        }

        .toggle-card-text {
          display: flex;
          flex-direction: column;
          gap: 2px;
          min-width: 0;
        }

        .toggle-card-title {
          font-weight: 600;
          font-size: 0.95rem;
          color: #212529;
        }

        .toggle-card-subtitle {
          font-size: 0.78rem;
          color: #868e96;
        }

        .toggle-switch {
          position: relative;
          flex-shrink: 0;
          width: 44px;
          height: 24px;
          border-radius: 999px;
          background-color: #ced4da;
          transition: background-color 0.2s ease;
        }

        .toggle-switch-input {
          position: absolute;
          inset: 0;
          margin: 0;
          opacity: 0;
          cursor: pointer;
        }

        .toggle-switch-knob {
          position: absolute;
          top: 2px;
          left: 2px;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background-color: #ffffff;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
          transition: transform 0.2s ease;
          pointer-events: none;
        }

        .toggle-switch-on .toggle-switch-knob {
          transform: translateX(20px);
        }

        .toggle-switch-on.toggle-success { background-color: #198754; }
        .toggle-switch-on.toggle-info { background-color: #0dcaf0; }
        .toggle-switch-on.toggle-primary { background-color: #0d6efd; }

        .toggle-card-on.toggle-success { color: #198754; }
        .toggle-card-on.toggle-info { color: #0a94ab; }
        .toggle-card-on.toggle-primary { color: #0d6efd; }
      `}</style>
    </Modal>
  )
}

export default ExhibitorsModal